// Frontend mirror of functions/api/_lib/project-transition-schemas.js. Same
// shapes — see that file's note on why the duplication exists (workers
// runtime can't pull in jsx imports; deliberate code duplication keeps the
// schema in lockstep with the validator on the same wire shape).

const ROLES = ['principal', 'assistant_principal', 'superintendent', 'parent_coordinator', 'other'];
const DOMAIN_OWNERS = ['school', 'nexsav_purchased', 'external'];
const RECURRING_CADENCES = ['monthly', 'quarterly', 'biannual', 'annual'];

export const PROJECT_TRANSITION_SCHEMAS = Object.freeze({
  media_capture: {
    fields: [
      // Same shape as the portal media booking form: start date & time plus a
      // duration (30-min steps, 4h cap). With an assignee, the shoot reserves
      // the photographer on the shared calendar so paid events and website
      // shoots cannot double-book them.
      { key: 'shoot_start_at', label: 'Shoot start (date & time)', type: 'datetime', required: false },
      { key: 'shoot_duration_hours', label: 'Shoot duration', type: 'select',
        options: ['1', '1.5', '2', '2.5', '3', '3.5', '4'], required: false },
      { key: 'media_subtypes', label: 'Media subtypes', type: 'multi_select',
        options: ['photography', 'drone_video', 'promo_video', 'none'], required: true },
      // A Media-role user, picked from a list (never a typed id).
      { key: 'assignee_user_id', label: 'Assignee (media staff)', type: 'media_user', required: false },
      { key: 'school_contact', label: 'School contact', type: 'text', required: true },
      { key: 'parent_consent_confirmed', label: 'Parent consent confirmed', type: 'checkbox', required: true },
    ],
  },
  build: {
    fields: [
      { key: 'brand_assets_received', label: 'Brand assets received', type: 'checkbox', required: true },
      { key: 'content_migration_source', label: 'Content migration source', type: 'text', required: true },
      { key: 'build_owner_user_id', label: 'Build owner', type: 'text', required: false },
      { key: 'target_preview_date', label: 'Target preview date', type: 'date', required: true },
    ],
  },
  preview_review: {
    fields: [
      { key: 'preview_url', label: 'Preview URL', type: 'text', required: true },
      { key: 'email_sent_date', label: 'Preview email sent date', type: 'date', required: false },
      { key: 'target_approval_date', label: 'Target approval date', type: 'date', required: true },
    ],
  },
  approved: {
    fields: [
      { key: 'approver_name', label: 'Approver name', type: 'text', required: true },
      { key: 'approver_role', label: 'Approver role', type: 'select', options: ROLES, required: true },
      { key: 'approval_date', label: 'Approval date', type: 'date', required: true },
      { key: 'approval_record_link', label: 'Approval record link', type: 'text', required: false },
    ],
  },
  domain_cutover: {
    fields: [
      { key: 'domain_id', label: 'Domain', type: 'text', required: false },
      { key: 'domain_owned_by', label: 'Domain ownership', type: 'select', options: DOMAIN_OWNERS, required: true },
      { key: 'purchased_cost_cents', label: 'Purchase cost (if NexSav-purchased)', type: 'currency', required: false },
      { key: 'dns_access_confirmed', label: 'DNS access confirmed', type: 'checkbox', required: true },
      { key: 'scheduled_cutover_datetime', label: 'Scheduled cutover (date)', type: 'date', required: true },
    ],
  },
  live: {
    fields: [
      { key: 'actual_go_live_datetime', label: 'Actual go-live (date)', type: 'date', required: true },
      { key: 'completion_email_sent', label: 'Completion email sent', type: 'checkbox', required: false },
      { key: 'client_trained_on_portal', label: 'Client trained on portal', type: 'checkbox', required: false },
    ],
  },
  maintained: {
    fields: [
      { key: 'maintenance_term_start_date', label: 'Maintenance term start date', type: 'date', required: true },
      { key: 'recurring_check_in_cadence', label: 'Check-in cadence', type: 'select', options: RECURRING_CADENCES, required: false },
    ],
  },
  canceled: { fields: [] },
  on_hold: { fields: [] },
});

export function projectTransitionSchema(stage) {
  return PROJECT_TRANSITION_SCHEMAS[stage] || { fields: [] };
}
